import { useState, useEffect, useMemo, Fragment } from 'react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Switch } from '@/components/ui-kit/switch';
import { useStepper } from '@/components/stepper/stepper-provider';
import { useCreateApiContext } from '../create-api-context';
import { Plus, Trash2, ChevronDown, ChevronRight, PlusCircle } from 'lucide-react';
import { useCreateApiAction } from '@/modules/gpt-chats/hooks/use-tools';

import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { useParams, useSearchParams } from '@/lib/react-router-compat';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { ParameterFormField } from './utils/tree-utils';
import { useParameterTree } from './hooks/use-parameter-tree';
import { buildParameters, reconstructTree } from './utils/schema-utils';
import { validateParameterName } from './utils/naming-utils';
import { useTranslation } from 'react-i18next';

export const CreateApiOutputParametersForm = ({ toolId }: { toolId?: string }) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const resolvedToolId = toolId || (params.toolId as string);
  const { nextStep } = useStepper();
  const { t } = useTranslation();
  const { formData, updateFormData } = useCreateApiContext();
  const { mutateAsync: createApiAction, isPending } = useCreateApiAction();
  const [parameterErrors, setParameterErrors] = useState<Record<string, string>>({});

  const {
    parameters,
    setParameters,
    handleAddParameter,
    handleAddChildParameter,
    handleRemoveParameter,
    handleParameterChange,
    toggleExpand,
  } = useParameterTree([
    {
      id: '1',
      name: '',
      description: '',
      type: 'string',
      location: 'body',
      defaultValue: '',
      required: false,
    },
  ]);

  const namingErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const validate = (params: ParameterFormField[], parentType = '') => {
      params.forEach((p) => {
        const isArrayItem = parentType === 'array';
        if (!isArrayItem) {
          const err = validateParameterName(p.name, 'body'); // Output parameters are always in body
          if (err) errors[p.id] = t(err.message as any);
        }
        if (p.children) validate(p.children, p.type);
      });
    };

    validate(parameters);
    return errors;
  }, [parameters, t]);

  const hasNamingErrors = Object.keys(namingErrors).length > 0;

  const nestedObjectLevel = 3;

  useEffect(() => {
    if (formData.output_parameters && Object.keys(formData.output_parameters).length > 0) {
      try {
        const loadedParams = reconstructTree(formData.output_parameters, {
          forceBodyLocation: true,
        });
        if (loadedParams.length > 0) setParameters(loadedParams);
      } catch (e) {
        console.error('Failed to load output parameters', e);
      }
    }
  }, [formData.output_parameters, setParameters]);

  const handleSubmit = async () => {
    // Check for duplicates in non-empty parameters
    const validParams = parameters.filter((p) => p.name && p.name.trim() !== '');
    const uniqueNames = new Set(validParams.map((p) => p.name));

    if (uniqueNames.size !== validParams.length) {
      showErrorToast({
        errors: { error: t('PARAMETER_NAMES_UNIQUE') },
      });
      return;
    }

    setParameterErrors({});
    const outputParameters = buildParameters(parameters, true); // true = ignore default values
    updateFormData({ output_parameters: outputParameters });

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        method: formData.method,
        endpoint: formData.endpoint,
        input_parameters: formData.input_parameters,
        output_parameters: outputParameters,
        is_open_tool: isBlocksTool,
      };

      const response = await createApiAction({
        toolId: resolvedToolId,
        actionId: formData.actionId,
        payload,
      });

      if (response.is_success) {
        showSuccessToast({ description: t('API_ACTION_UPDATED_SUCCESSFULLY') });
        if (response.item_id) {
          updateFormData({ actionId: response.item_id });
        }
        nextStep();
      } else {
        showErrorToast({ errors: response.detail });
      }
    } catch (error) {
      showErrorToast({ errors: ErrorTransformer(error) });
    }
  };

  const renderRows = (
    params: ParameterFormField[],
    level = 0,
    objectDepth = 0,
    parentType = ''
  ) => {
    return params.map((param, index) => {
      const isArrayItem = parentType === 'array';

      return (
        <Fragment key={param.id}>
          <TableRow
            className={`border-b-0 hover:bg-transparent ${
              level === 0 && index > 0 ? '!border-t !border-border' : ''
            }`}
          >
            <TableCell>
              <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 40}px` }}>
                {param.type === 'array' || param.type === 'object' ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 p-0"
                    onClick={() => toggleExpand(param.id)}
                  >
                    {param.isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                ) : null}
                <div className="flex-1 space-y-1">
                  <Input
                    placeholder={isArrayItem ? t('ARRAY_ITEM') : t('ENTER_NAME')}
                    value={isArrayItem ? '' : param.name}
                    disabled={isArrayItem}
                    maxLength={100}
                    onChange={(e) => handleParameterChange(param.id, 'name', e.target.value)}
                    className={parameterErrors[param.id] ? 'border-error' : ''}
                  />
                  {parameterErrors[param.id] && (
                    <p className="text-xs text-error">{parameterErrors[param.id]}</p>
                  )}
                  {namingErrors[param.id] && (
                    <p className="text-xs text-error">{namingErrors[param.id]}</p>
                  )}
                  {!isArrayItem && param.name.length >= 100 && (
                    <p className="text-[12px] text-error">{t('MAX_CHARACTERS', { count: 100 })}</p>
                  )}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div className="space-y-1">
                <Input
                  placeholder={t('ENTER_DESCRIPTION')}
                  value={param.description}
                  maxLength={100}
                  onChange={(e) => handleParameterChange(param.id, 'description', e.target.value)}
                />
                {(param.description || '').length >= 100 && (
                  <p className="text-[12px] text-error">{t('MAX_CHARACTERS', { count: 100 })}</p>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Select
                value={param.type}
                onValueChange={(value) => handleParameterChange(param.id, 'type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('SELECT')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">String</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="integer">Integer</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  {objectDepth < nestedObjectLevel && (
                    <SelectItem value="object">Object</SelectItem>
                  )}
                  {level < 10 && !isArrayItem && <SelectItem value="array">Array</SelectItem>}
                </SelectContent>
              </Select>
            </TableCell>
            <TableCell>
              <Switch
                checked={param.required}
                onCheckedChange={(checked) => handleParameterChange(param.id, 'required', checked)}
              />
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveParameter(param.id)}
                  disabled={(parameters.length === 1 && level === 0) || isArrayItem}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                {param.type === 'object' && level < 10 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleAddChildParameter(param.id, 'body')}
                    className="text-primary"
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>

          {(param.type === 'object' || param.type === 'array') &&
            param.isExpanded &&
            param.children &&
            renderRows(
              param.children,
              level + 1,
              param.type === 'object' ? objectDepth + 1 : objectDepth,
              param.type
            )}
        </Fragment>
      );
    });
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">{t('OUTPUT_PARAMETERS')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('OUTPUT_PARAMETERS_DESCRIPTION')}</p>
      </div>

      <div className="rounded-lg border bg-background">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[180px]">{t('PARAM_NAME')}</TableHead>
              <TableHead className="w-[200px]">{t('DESCRIPTION')}</TableHead>
              <TableHead className="w-[140px]">{t('TYPE')}</TableHead>
              <TableHead className="w-[100px]">{t('REQUIRED')}</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{renderRows(parameters)}</TableBody>
        </Table>

        <div className="p-6 pt-2">
          <Button variant="ghost" onClick={handleAddParameter} className="text-primary">
            <Plus className="mr-2 h-4 w-4" />
            {t('ADD_PARAMETER')}
          </Button>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-start gap-3">
        <Button onClick={handleSubmit} className="px-8" disabled={isPending || hasNamingErrors}>
          {isPending ? t('SAVING') : t('SAVE_AND_DEBUG')}
        </Button>
      </div>
    </div>
  );
};
